import { addPoses, lerpPoses, type Pose } from "./pose.js";
import { SEATED } from "./seated.js";

/** Quanto cada mão segura o cabo (1 = na vassoura, 0 = livre, guiada pela pose). */
export interface Grip {
  l: number;
  r: number;
}

export interface Key {
  t: number;
  /** Camada somada à pose sentada. */
  layer: Pose;
  grip?: Grip;
}

export interface Clip {
  id: string;
  label: string;
  duration: number;
  loop: boolean;
  keys: readonly Key[];
}

const BOTH: Grip = { l: 1, r: 1 };
const RIGHT_FREE: Grip = { l: 1, r: 0 };
const FREE: Grip = { l: 0, r: 0 };

function clip(id: string, label: string, duration: number, loop: boolean, keys: Key[]): Clip {
  return { id, label, duration, loop, keys };
}

/** Os 12 clipes do item 2.5. O voo (inclinação, arfagem, balanço) é procedural e fica em `flight.ts`. */
export const CLIPS = {
  sentado: clip("sentado", "Sentado (respirando)", 2.4, true, [
    { t: 0, layer: {} },
    { t: 1.2, layer: { spine_02: [-2, 0, 0], spine_03: [-2, 0, 0], Head: [2, 0, 0] } },
    { t: 2.4, layer: {} },
  ]),
  curva: clip("curva", "Curva (peso 1 = esquerda)", 1, true, [
    {
      t: 0,
      layer: {
        spine_01: [0, 0, -8],
        spine_02: [0, 0, -10],
        spine_03: [0, 6, -8],
        neck_01: [0, 0, 10],
        Head: [0, 8, 10],
      },
    },
    {
      t: 1,
      layer: {
        spine_01: [0, 0, -8],
        spine_02: [0, 0, -10],
        spine_03: [0, 6, -8],
        neck_01: [0, 0, 10],
        Head: [0, 8, 10],
      },
    },
  ]),
  arremesso: clip("arremesso", "Arremesso", 0.8, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.3,
      layer: {
        spine_02: [-8, -14, 0],
        spine_03: [-6, -14, 0],
        upperarm_r: [0, -80, -140],
        lowerarm_r: [0, 70, 0],
        hand_r: [0, 0, 25],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.42,
      layer: {
        spine_02: [2, 12, 0],
        spine_03: [2, 12, 0],
        upperarm_r: [0, -10, -95],
        lowerarm_r: [0, -10, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.6,
      layer: {
        spine_02: [6, 18, 0],
        spine_03: [4, 14, 0],
        upperarm_r: [0, 10, -35],
        lowerarm_r: [0, -15, 0],
      },
      grip: RIGHT_FREE,
    },
    { t: 0.8, layer: {}, grip: BOTH },
  ]),
  passe: clip("passe", "Passe", 0.5, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.18,
      layer: { spine_03: [0, -10, 0], upperarm_r: [0, -45, -70], lowerarm_r: [0, 55, 0] },
      grip: RIGHT_FREE,
    },
    {
      t: 0.3,
      layer: { spine_03: [0, 10, 0], upperarm_r: [0, 0, -55], lowerarm_r: [0, -20, 0] },
      grip: RIGHT_FREE,
    },
    { t: 0.5, layer: {}, grip: BOTH },
  ]),
  rebatida: clip("rebatida", "Rebatida (taco)", 0.7, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.28,
      layer: {
        spine_02: [-6, -20, 0],
        spine_03: [-6, -22, 0],
        upperarm_r: [0, -95, -95],
        lowerarm_r: [0, 60, 0],
        hand_r: [30, 0, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.4,
      layer: {
        spine_02: [0, 18, 0],
        spine_03: [0, 22, 0],
        upperarm_r: [0, 15, -80],
        lowerarm_r: [0, -15, 0],
        hand_r: [-20, 0, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.55,
      layer: { spine_02: [4, 26, 0], spine_03: [2, 28, 0], upperarm_r: [0, 45, -60], lowerarm_r: [0, 10, 0] },
      grip: RIGHT_FREE,
    },
    { t: 0.7, layer: {}, grip: BOTH },
  ]),
  defesa: clip("defesa", "Defesa do goleiro", 0.6, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.18,
      layer: {
        spine_01: [-14, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [12, 0, 0],
        upperarm_l: [0, 50, 40],
        lowerarm_l: [0, 20, 0],
        upperarm_r: [0, -50, -40],
        lowerarm_r: [0, -20, 0],
      },
      grip: FREE,
    },
    {
      t: 0.42,
      layer: {
        spine_01: [-14, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [12, 0, 0],
        upperarm_l: [0, 50, 40],
        lowerarm_l: [0, 20, 0],
        upperarm_r: [0, -50, -40],
        lowerarm_r: [0, -20, 0],
      },
      grip: FREE,
    },
    { t: 0.6, layer: {}, grip: BOTH },
  ]),
  agarrar: clip("agarrar", "Agarrar a Pelota", 0.45, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.2,
      layer: {
        spine_02: [-6, 0, 0],
        upperarm_r: [0, -20, -90],
        lowerarm_r: [0, -15, 0],
        hand_r: [0, 0, -20],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.3,
      layer: { spine_02: [-4, 0, 0], upperarm_r: [0, -10, -70], lowerarm_r: [0, 40, 0] },
      grip: RIGHT_FREE,
    },
    { t: 0.45, layer: {}, grip: BOTH },
  ]),
  comemorar: clip("comemorar", "Comemorar", 1.4, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.25,
      layer: {
        spine_01: [-12, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [14, 0, 0],
        upperarm_r: [0, -55, -150],
        lowerarm_r: [0, 20, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.5,
      layer: {
        spine_01: [-12, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [14, 0, 0],
        upperarm_r: [0, -55, -120],
        lowerarm_r: [0, 70, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 0.75,
      layer: {
        spine_01: [-12, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [14, 0, 0],
        upperarm_r: [0, -55, -150],
        lowerarm_r: [0, 20, 0],
      },
      grip: RIGHT_FREE,
    },
    {
      t: 1.0,
      layer: {
        spine_01: [-12, 0, 0],
        spine_02: [-10, 0, 0],
        neck_01: [14, 0, 0],
        upperarm_r: [0, -55, -120],
        lowerarm_r: [0, 70, 0],
      },
      grip: RIGHT_FREE,
    },
    { t: 1.4, layer: {}, grip: BOTH },
  ]),
  atordoado: clip("atordoado", "Atordoado (Petardo)", 2.0, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.15,
      layer: { spine_01: [-10, 0, 10], spine_02: [-8, 0, 8], neck_01: [20, 0, 15], Head: [15, 10, 10] },
      grip: { l: 1, r: 0.3 },
    },
    {
      t: 0.6,
      layer: { spine_01: [10, 0, -4], spine_02: [6, 0, -4], neck_01: [25, 0, -12], Head: [20, -10, -12] },
      grip: { l: 1, r: 0.5 },
    },
    {
      t: 1.2,
      layer: { spine_01: [8, 0, 4], spine_02: [6, 0, 4], neck_01: [22, 0, 10], Head: [18, 8, 8] },
      grip: { l: 1, r: 0.7 },
    },
    { t: 2.0, layer: {}, grip: BOTH },
  ]),
  mergulho: clip("mergulho", "Mergulho (caçador)", 0.6, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.3,
      layer: {
        spine_01: [18, 0, 0],
        spine_02: [12, 0, 0],
        spine_03: [8, 0, 0],
        neck_01: [-22, 0, 0],
        Head: [-14, 0, 0],
        thigh_l: [-12, 0, 0],
        thigh_r: [-12, 0, 0],
        calf_l: [20, 0, 0],
        calf_r: [20, 0, 0],
      },
      grip: BOTH,
    },
    {
      t: 0.6,
      layer: {
        spine_01: [18, 0, 0],
        spine_02: [12, 0, 0],
        spine_03: [8, 0, 0],
        neck_01: [-22, 0, 0],
        Head: [-14, 0, 0],
        thigh_l: [-12, 0, 0],
        thigh_r: [-12, 0, 0],
        calf_l: [20, 0, 0],
        calf_r: [20, 0, 0],
      },
      grip: BOTH,
    },
  ]),
  turbo: clip("turbo", "Turbo", 0.35, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.35,
      layer: { spine_01: [10, 0, 0], spine_02: [8, 0, 0], neck_01: [-12, 0, 0], Head: [-8, 0, 0] },
      grip: BOTH,
    },
  ]),
  olhar: clip("olhar", "Olhar para trás", 1.0, false, [
    { t: 0, layer: {}, grip: BOTH },
    {
      t: 0.3,
      layer: { spine_02: [-6, 18, 0], spine_03: [-6, 22, 0], neck_01: [6, 30, 0], Head: [4, 35, 0] },
      grip: { l: 1, r: 0.6 },
    },
    {
      t: 0.7,
      layer: { spine_02: [-6, 18, 0], spine_03: [-6, 22, 0], neck_01: [6, 30, 0], Head: [4, 35, 0] },
      grip: { l: 1, r: 0.6 },
    },
    { t: 1.0, layer: {}, grip: BOTH },
  ]),
} as const satisfies Record<string, Clip>;

export type ClipId = keyof typeof CLIPS;

export interface Sample {
  pose: Pose;
  grip: Grip;
}

const smooth = (t: number) => t * t * (3 - 2 * t);

/** Amostra um clipe no tempo `t` (segundos) e devolve a pose completa (sentado + camada) e a pegada. */
export function sampleClip(c: Clip, t: number, weight = 1): Sample {
  const time = c.loop ? ((t % c.duration) + c.duration) % c.duration : Math.min(Math.max(t, 0), c.duration);
  let i = 0;
  while (i < c.keys.length - 2 && (c.keys[i + 1]?.t ?? Infinity) <= time) i++;
  const a = c.keys[i] as Key;
  const b = (c.keys[i + 1] ?? a) as Key;
  const span = b.t - a.t;
  const u = span > 0 ? smooth(Math.min(1, Math.max(0, (time - a.t) / span))) : 0;
  const layer = lerpPoses(a.layer, b.layer, u);
  const ga = a.grip ?? BOTH;
  const gb = b.grip ?? ga;
  const grip = {
    l: 1 + (ga.l + (gb.l - ga.l) * u - 1) * weight,
    r: 1 + (ga.r + (gb.r - ga.r) * u - 1) * weight,
  };
  return { pose: addPoses(SEATED, layer, weight), grip };
}
